import { apiService } from './api';
import { User, ApiResponse } from '../types';

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_data';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  // Login
  async login(credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> {
    return apiService.post('/users/sign-in', {
      ...credentials,
      rememberMe: credentials.rememberMe ?? true,
    });
  }

  // Logout
  async logout(): Promise<ApiResponse<void>> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await apiService.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      this.clearAuthData();
    }

    return { success: true, data: undefined, message: 'Logged out successfully' };
  }

  // Get Current User
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiService.get('/auth/me');
  }

  // Refresh Token
  async refreshToken(): Promise<ApiResponse<RefreshTokenResponse>> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiService.post('/auth/refresh', { refreshToken });

    if (response.success && response.data) {
      const data = response.data as RefreshTokenResponse;
      this.setTokens(data.token, data.refreshToken, data.expiresIn);
    }

    return response as ApiResponse<RefreshTokenResponse>;
  }

  // Forgot Password
  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<void>> {
    return apiService.post('/auth/password-reset/request', data);
  }

  // Reset Password
  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>> {
    return apiService.post('/auth/password-reset/confirm', {
      resetToken: data.token,
      newPassword: data.password
    });
  }

  // Update Profile (authenticated)
  async updateProfile(name: string, surname: string): Promise<ApiResponse<User>> {
    const response = await apiService.post<any>('/users/update-profile', { name, surname });

    if (response.success && response.data) {
      const data = response.data;
      const normalizedUser: User = {
        id: data.id || data.Id,
        email: data.email || data.Email,
        name: data.name || data.Name,
        surname: data.surname || data.Surname || '',
        role: data.role || data.Role || 'business_owner',
        termsAccepted: data.termsAccepted ?? data.TermsAccepted ?? false,
        termsVersion: data.termsVersion || data.TermsVersion || '',
        termsAcceptedAt: data.termsAcceptedAt || data.TermsAcceptedAt || null,
        createdAt: data.createdAt || data.CreatedAt || new Date().toISOString(),
        updatedAt: data.updatedAt || data.UpdatedAt || new Date().toISOString(),
      };

      this.setUser(normalizedUser);
      return { success: true, data: normalizedUser };
    }

    return response;
  }

  // Change Password (authenticated)
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    return apiService.post('/users/change-password', { currentPassword, newPassword });
  }

  // Token Management
  setAuthData(user: User, token: string, refreshToken: string, expiresIn: number): void {
    this.setUser(user);
    this.setTokens(token, refreshToken, expiresIn);
  }

  private setTokens(token: string, refreshToken: string, expiresIn: number): void {
    // Token zorunlu, refreshToken opsiyonel
    if (!token) {
      console.warn('[AuthService] Attempted to set invalid tokens - token is required');
      return;
    }
    localStorage.setItem(this.TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
    // expiresIn undefined/NaN kontrolü
    const expiry = typeof expiresIn === 'number' && !isNaN(expiresIn)
      ? Date.now() + expiresIn * 1000
      : Date.now() + 3600 * 1000; // Default 1 hour
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiry.toString());
  }

  private setUser(user: User): void {
    if (!user || typeof user !== 'object') {
      console.warn('[AuthService] Attempted to set invalid user data');
      return;
    }
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  // Getters
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!token || !expiry) return false;

    // Check if token is expired
    const isExpired = Date.now() >= parseInt(expiry);
    if (isExpired) {
      // Token expire olmuş ama refresh token'ı temizleme
      // Çünkü initializeAuth() içinde refresh edilebilir
      // Sadece token'ı geçersiz olarak işaretle
      return false;
    }

    return true;
  }

  isTokenExpired(): boolean {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    return Date.now() >= parseInt(expiry);
  }

  // Token will expire in 5 minutes
  shouldRefreshToken(): boolean {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    const timeUntilExpiry = parseInt(expiry) - Date.now();
    return timeUntilExpiry < 5 * 60 * 1000; // 5 minutes
  }
}

export const authService = new AuthService();
export default authService;
